import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const testimonials = [
  {
    quote:
      "She is so good at listening and empathizing with both parties. She gives you realistic things to try at home.",
    source: "Couples therapy client",
  },
  {
    quote:
      "I was skeptical about therapy. She's easy to talk to, keeps it real, and somehow knows exactly when to push and when to just let me breathe.",
    source: "Individual therapy client",
  },
  {
    quote:
      "She can keep things light even when the subject is heavy. I laughed in sessions I expected to dread — and I left every one of them with something practical to try.",
    source: "Individual therapy client",
  },
];

const Testimonials = () => {
  usePageMeta(
    "Client Reflections | Holistic Healing Psychotherapy, Inc.",
    "What couples and individuals say about working with Breeza Y. Quezada, LCSW — warm, direct, nonjudgmental therapy in California."
  );

  return (
    <div data-testid="testimonials-page">
      <section className="bg-cream-50 pt-14 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="testimonials-eyebrow">Testimonials</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink max-w-3xl" data-testid="testimonials-headline">
              Words from people I've <em className="text-forest-700">walked alongside</em>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-2xl" data-testid="testimonials-subcopy">
              Therapy is private work, so these reflections are shared with permission — names and identifying
              details have been changed to protect confidentiality.
            </p>
          </FadeIn>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeIn key={i} delay={i * 0.07}>
                <figure
                  className="h-full rounded-2xl border border-forest-800/10 bg-cream-100 p-8 flex flex-col transition-all duration-500 hover:border-gold-500/50 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(18,55,42,0.12)]"
                  data-testid={`testimonial-card-${i + 1}`}
                >
                  <span className="font-serif text-5xl text-gold-500/70 leading-none mb-4">"</span>
                  <blockquote className="font-serif text-lg sm:text-xl text-forest-800 leading-relaxed flex-1">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-6 text-xs uppercase tracking-[0.2em] text-sage">— {t.source}</figcaption>
                </figure>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.2}>
            <div className="mt-16 text-center">
              <p className="font-serif italic text-2xl sm:text-3xl text-forest-800 max-w-2xl mx-auto leading-snug" data-testid="testimonials-close">
                "After nearly two decades in this work, witnessing change — even when people have begun to lose hope —
                is still the most meaningful part of what I do."
              </p>
              <Link
                to="/contact"
                data-testid="testimonials-schedule-cta"
                className="inline-flex items-center gap-2 mt-9 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Testimonials;
